import React, { useEffect, useState } from 'react';
import {
    Box, Grid, Button, Dialog, Typography, Paper, alpha, styled, FormControl, InputLabel, Select, MenuItem, Checkbox, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Card, CardContent, Divider
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import PrintIcon from '@mui/icons-material/Print';
import EventIcon from '@mui/icons-material/Event';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/axios';
import LinearProgress from '@mui/material/LinearProgress';

const GradientHeader = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(1),
    color: 'white',
}));

const PrintDialog = ({ open, onClose, selectedMeal, globalProperty }) => {
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [foodBomItem, setFoodBomItem] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [printData, setPrintData] = useState(null);
    const [showDataTable, setShowDataTable] = useState(false);
    const [groupedData, setGroupedData] = useState({});
    const [summaryData, setSummaryData] = useState([]);

    const formatDate = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const formatDisplayDate = (dateString) => {
        const date = new Date(dateString);
        const dayNum = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const weekday = date.toLocaleString('en-US', { weekday: 'long' });
        return `${dayNum} ${month} ${year} (${weekday})`;
    };

    const fetchFoodBomitem = async () => {
        try {
            const response = await axiosInstance.post('MstFood/GetdrpMstFoodBomItmNm', {
                "PropId": globalProperty || 2,
                "FromDt": "",
                "ToDt": "",
                "Flag": ""
            });

            const items = response.data.Data || [];
            setFoodBomItem(items);
        } catch (error) {
            toast.error('Error fetching food items');
        }
    };

    useEffect(() => {
        if (open && globalProperty) {
            fetchFoodBomitem();
            setShowDataTable(false);
            setPrintData(null);
            setGroupedData({});
        }
    }, [open, globalProperty]);

    const fetchPrintData = async () => {
        if (!fromDate || !toDate || selectedItems.length === 0) {
            toast.error('Please select both dates and at least one item');
            return;
        }

        setLoading(true);

        try {
            const payload = {
                "PropId": globalProperty || 2,
                "FromDt": formatDate(fromDate),
                "ToDt": formatDate(toDate),
                "Strwhere": selectedItems.join(','),
                "Flag": ""
            };

            const response = await axiosInstance.post('MstFood/RetriveBulkItmWisePersonDishAll', payload);

            if (response.data.Status === 0) {
                const foodpersonList = response.data.Data?.foodpersonList || [];
                setPrintData(foodpersonList);

                if (foodpersonList.length > 0) {
                    const grouped = groupDataByDateAndMeal(foodpersonList);
                    setGroupedData(grouped);
                    setShowDataTable(true);
                    fetchSummaryData();
                } else {
                    toast.warning('No data found for the selected criteria');
                    setShowDataTable(false);
                    setPrintData(null);
                    setGroupedData({});
                }
            } else {
                throw new Error(response.data.Message || 'No data found');
            }
        } catch (error) {
            toast.error(error.message || 'Error fetching data for printing');
            setShowDataTable(false);
            setPrintData(null);
            setGroupedData({});
        } finally {
            setLoading(false);
        }
    };

    // useEffect(() => {
    //     fetchSummaryData();
    // }, [fromDate, selectedItems])

    const fetchSummaryData = async () => {
        try {
            const payload = {
                PropId: globalProperty || 2,
                FromDt: formatDate(fromDate),
                ToDt: formatDate(toDate),
                Strwhere: selectedItems.join(','),
                Flag: "Sum"
            };

            const response = await axiosInstance.post('MstFood/RetriveBulkItmWisePersonDishAll', payload);
            if (response.data.Status === 0) {
                setSummaryData(response.data.Data?.foodpersonList || response.data.Data || []);
            } else {
                setSummaryData([])
            }
        } catch (error) {
            toast.error("Error while fetching record.")
        }
    };

    const groupDataByDateAndMeal = (data) => {
        return data.reduce((acc, item) => {
            const date = item.FoodWeekCalDt.split('T')[0];
            const mealType = item.MealTypeName;

            if (!acc[date]) {
                acc[date] = {};
            }
            if (!acc[date][mealType]) {
                acc[date][mealType] = [];
            }

            acc[date][mealType].push(item);
            return acc;
        }, {});
    };

    const handlePrint = () => {
        if (!printData || printData.length === 0) {
            toast.error('No data to print');
            return;
        }
        generatePrintContent(printData);
    };

    const handlePrintSummary = () => {
        if (!summaryData?.length) {
            toast.error('No summary data available. Please fetch data first.');
            return;
        }
        generateSummaryPrintContent(summaryData);
    };

    const generatePrintContent = (data) => {
        const printWindow = window.open('', '_blank');

        const mealTypeName = selectedMeal ?
            (selectedMeal === 1 ? 'Breakfast' :
                selectedMeal === 2 ? 'Lunch' :
                    selectedMeal === 3 ? 'Snacks' : 'Dinner') : 'All Meals';

        const groupedData = groupDataByDateAndMeal(data);

        let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Day Wise Meal Plan</title>
            <style>
                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }
                
                body {
                    font-family: Arial, sans-serif;
                    font-size: 12px;
                    line-height: 1.2;
                    padding: 5px;
                }
                
                .header {
                    text-align: center;
                    margin-bottom: 5px;
                    padding: 5px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: black;
                    border-radius: 3px;
                }
                
                .header h1 {
                    font-size: 16px;
                    margin: 2px 0;
                }
                
                .header p {
                    font-size: 11px;
                    margin: 2px 0;
                }
                
                .info-row {
                    display: flex;
                    justify-content: space-between;
                    background: #f3f4f6;
                    padding: 4px 8px;
                    border-radius: 3px;
                    margin-bottom: 5px;
                    font-size: 11px;
                }
                
                .meal-section {
                    margin-bottom: 8px;
                    page-break-inside: avoid;
                    border: 1px solid #e5e7eb;
                    border-radius: 3px;
                    overflow: hidden;
                }
                
                .meal-title {
                    background: #667eea;
                    color: white;
                    padding: 3px 6px;
                    font-size: 12px;
                    font-weight: bold;
                }
                
                .date-title {
                    background: #f3f4f6;
                    padding: 3px 6px;
                    margin-top: 5px;
                    font-weight: bold;
                    color: #4b5563;
                    font-size: 10px;
                    border-left: 3px solid #764ba2;
                }
                
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                }
                
                th, td {
                    border: 1px solid #d1d5db;
                    padding: 3px 4px;
                    text-align: left;
                }
                
                th {
                    background-color: #f9fafb;
                    font-weight: bold;
                    white-space: nowrap;
                }
                
                td {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100px;
                }
                
                tr:nth-child(even) {
                    background-color: #f9f9f9;
                }
                
                .footer {
                    text-align: center;
                    margin-top: 5px;
                    padding: 3px;
                    font-size: 7px;
                    color: #6b7280;
                    border-top: 1px solid #e5e7eb;
                }
                
                .summary-badge {
                    background: #e8f5e9;
                    padding: 2px 6px;
                    border-radius: 10px;
                    font-size: 10px;
                    font-weight: bold;
                    color: #059669;
                }
                
                .selected-items {
                    background: #fff3e0;
                    padding: 3px 6px;
                    border-radius: 3px;
                    font-size: 10px;
                    margin-bottom: 5px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                
                @media print {
                    body {
                        padding: 2px;
                    }
                    .no-print {
                        display: none !important;
                    }
                    .meal-section {
                        break-inside: avoid;
                    }
                }
    
                .col-food { max-width: 80px; }
                .col-item { max-width: 70px; }
                .col-qty { max-width: 30px; }
                .col-remark { max-width: 50px; }
                
                @media (max-width: 600px) {
                    table { font-size: 10px; }
                    th, td { padding: 2px; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>Day Wise Meal Plan</h1>
                <p>${data[0]?.PropName || globalProperty || 'All Properties'}</p>
            </div>
            
            <div class="info-row">
                <span><strong>From:</strong> ${formatDate(fromDate)}</span>
                <span><strong>To:</strong> ${formatDate(toDate)}</span>
                <span><strong>Meal:</strong> ${mealTypeName}</span>
                <span class="summary-badge">${data.length} Records</span>
            </div>

            <!-- Compact Selected Items -->
            <div class="selected-items">
                <strong>Person(s):</strong> ${selectedItems.join(', ')}
            </div>
        `;

        Object.keys(groupedData).sort().forEach(date => {
            htmlContent += `<div class="date-title">${formatDisplayDate(date)}</div>`;

            Object.keys(groupedData[date]).sort().forEach(mealType => {
                const items = groupedData[date][mealType];

                htmlContent += `
                <div class="meal-section">
                    <div class="meal-title">${mealType}</div>
                    <table>
                        <thead>
                            <tr>
                                <th class="col-food">Food</th>
                                <th class="col-item">Item</th>
                                <th class="col-qty">No. of person</th>
                                <th class="col-qty">Qty</th>
                                <th class="col-remark">Remark</th>
                            </tr>
                        </thead>
                        <tbody>
                    `;

                items.forEach(item => {
                    const foodName = item.FoodName?.length > 15 ? item.FoodName.substring(0, 25) + '..' : item.FoodName || '-';
                    const itemName = item.ItmName?.length > 12 ? item.ItmName.substring(0, 20) + '..' : item.ItmName || '-';
                    const remark = item.Remark?.length > 10 ? item.Remark.substring(0, 10) + '..' : item.Remark || '-';

                    htmlContent += `
                    <tr>
                        <td class="col-food" title="${item.FoodName || ''}">${foodName}</td>
                        <td class="col-item" title="${item.ItmName || ''}">${itemName}</td>
                        <td class="col-qty">${item.EQNm0 || '-'}</td>
                        <td class="col-qty">${item.EQ0 || 0}</td>
                        <td class="col-remark" title="${item.Remark || ''}">${remark}</td>
                    </tr>
                `;
                });

                htmlContent += `
                        </tbody>
                    </table>
                </div>
            `;
            });
        });

        htmlContent += `
            <div class="footer">
                <span>Generated: ${new Date().toLocaleString()}</span> | 
                <span>Computer Generated Document</span>
            </div>
            
            <div class="no-print" style="text-align: center; margin-top: 5px;">
                <button onclick="window.print()" style="padding: 4px 10px; background: #667eea; color: white; border: none; border-radius: 3px; cursor: pointer; margin: 0 2px; font-size: 12px;">
                    Print
                </button>
                <button onclick="window.close()" style="padding: 4px 10px; background: #6c757d; color: white; border: none; border-radius: 3px; cursor: pointer; margin: 0 2px; font-size: 12px;">
                    Close
                </button>
            </div>
        </body>
        </html>
    `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        setTimeout(() => {
            printWindow.print();
        }, 200);
    };

    const generateSummaryPrintContent = (data) => {
        if (!data || data.length === 0) {
            toast.error("No summary data to print");
            return;
        }

        const printWindow = window.open(
            '',
            '_blank',
            'width=1000,height=600,top=100,left=100,scrollbars=yes,resizable=yes'
        );

        const mealTypeName = selectedMeal ?
            (selectedMeal === 1 ? 'Breakfast' :
                selectedMeal === 2 ? 'Lunch' :
                    selectedMeal === 3 ? 'Snacks' : 'Dinner') : 'All Meals';

        // Dynamically find EQ fields from the first item
        const firstItem = data[0] || {};
        const eqNmKey = Object.keys(firstItem).find(key => key.startsWith('EQNm')) || 'EQNm4';
        const eqKey = Object.keys(firstItem).find(key => key.startsWith('EQ') && !key.startsWith('EQNm')) || 'EQ4';

        const firstItemTotalPersons = data[1][eqNmKey] !== undefined ? data[1][eqNmKey] : '-';

        let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Ingredients Requirement Sheet</title>
            <style>
                body {
                    font-family: Arial, sans-serif;
                    font-size: 12px;
                    padding: 5px;
                    margin: 0;
                }
                .header {
                    text-align: center;
                    background: linear-gradient(135deg, #667eea, #764ba2);
                    color: white;
                    padding: 6px;
                    border-radius: 6px;
                    margin-bottom: 15px;
                    -webkit-print-color-adjust: exact; /* ensures color prints in Chrome */
                    print-color-adjust: exact; /* ensures color prints in other browsers */
                }
                .header h1 { 
                    margin: 5px 0 3px 0; 
                    font-size: 18px; 
                }
                table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                    margin-top: 10px;
                }
                th, td {
                    border: 1px solid #999;
                    padding: 8px 6px;
                    text-align: left;
                }
                th {
                    background-color: #f0f0f0;
                    font-weight: bold;
                }
                .qty-column {
                    text-align: center;
                    font-weight: 500;
                }
                .persons-column {
                    text-align: center;
                    font-weight: 500;
                }
                .property{
                    font-weight: 600
                }
                .footer {
                    text-align: center;
                    margin-top: 20px;
                    font-size: 10px;
                    color: #666;
                }

                /* Optional: solid color fallback for printers that ignore gradients */
                @media print {
                    .header {
                        background: #667eea !important; 
                        color: white !important;
                    }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>Ingredients Requirement Sheet</h1>  
                <p class='property'>${summaryData[0]?.PropName || 'Clanbridge Living'}</p>
            </div>

            <table style="width: 100%; border: 1px solid #999; border-radius: 6px; margin-bottom: 15px; border-collapse: collapse;">
                <tbody>
                    <tr style="background-color: #f0f0f0;">
                        <td style="padding: 8px; font-weight: bold;">Meal Type</td>
                        <td style="padding: 8px;">${mealTypeName}</td>
                        <td style="padding: 8px; font-weight: bold;">Total Persons/Plates</td>
                        <td style="padding: 8px;">${firstItemTotalPersons}</td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; font-weight: bold;">Date</td>
                        <td style="padding: 8px;">${formatDisplayDate(fromDate)}</td>
                        <td style="padding: 8px; font-weight: bold;">Print Date</td>
                        <td style="padding: 8px;">${formatDisplayDate(toDate)}</td>
                    </tr>
                </tbody>
            </table>

            <table>
                <thead>
                    <tr>
                        <th style="width: 30%;">Ingredients</th>
                         <!-- <th style="width: 15%;" class="persons-column">Total Persons</th> -->
                        <th style="width: 15%;" class="qty-column">Quantity</th>
                        <th style="width: 12%; class="persons-column">Unit</th>
                        <th style="width: 30%;">Remark</th>
                    </tr>
                </thead>
                <tbody>
            `;

        data.forEach(item => {
            const itemName = item.ItmName || '-';
            const unit = item.UnitName || '-';
            const remark = item.Remark || '-';

            // Dynamic values
            // const totalPersons = item[eqNmKey] !== undefined ? item[eqNmKey] : '-';
            const quantity = item[eqKey] !== undefined ? item[eqKey] : 0;

            htmlContent += `
            <tr>
                <td>${itemName}</td>
                <td class="qty-column">${quantity}</td>
                <td class="persons-column">${unit}</td>
                <td>${remark}</td>
            </tr>`;
        });

        htmlContent += `
            </tbody>
            </table>

            <div class="footer" style="margin-top: 40px; font-size: 12px; color: #333;">
                <p style="text-align: center; font-weight: 600; margin-bottom: 30px;">
                    Declaration: This is to certify that the ingredients listed above are prepared and calculated as per the required standards and meal plan.
                </p>

                <div style="display: flex; justify-content: space-between; margin-top: 50px; padding: 0 40px;">
                    <div style="text-align: center;">
                        <div style="border-top: 1px solid #000; width: 200px; margin: 0 auto;"></div>
                        <p style="margin: 5px 0 0 0;">Prepared By</p>
                        <p style="margin: 5px 0 0 0;">Date: ${new Date().toLocaleDateString()}</p>
                    </div>

                    <div style="text-align: center;">
                        <div style="border-top: 1px solid #000; width: 200px; margin: 0 auto;"></div>
                        <p style="margin: 5px 0 0 0;">Authorized Signatory</p>
                        <p style="margin: 5px 0 0 0;">Date: ${new Date().toLocaleDateString()}</p>
                    </div>
                </div>

                <p style="text-align: center; margin-top: 30px; font-size: 10px; color: #666;">
                    Thank you for choosing Clanbridge Living Pvt Ltd
                </p>
                <p style="text-align: center; margin-top: 3px; font-size: 10px; color: #666;">
                    © 2024-2026 Clanbridge Living Pvt Ltd. All Rights Reserved.
                </p>

                <p style="text-align: center; margin-top: 30px; font-size: 10px; color: #666;">
                    Generated on ${new Date().toLocaleString()} | Computer Generated Document
                </p>
            </div>

           <!-- <div class="no-print" style="text-align: center; margin-top: 15px;">
                <button onclick="window.print()" style="padding: 6px 14px; background: #667eea; color: white; border: none; border-radius: 4px; cursor: pointer; margin: 5px;">
                    Print
                </button>
                <button onclick="window.close()" style="padding: 6px 14px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin: 5px;">
                    Close
                </button>
            </div> -->
        </body>
        </html>`;

        printWindow.document.write(htmlContent);
        printWindow.document.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 300);
    };

    const handlePersonChange = (event) => {
        const value = event.target.value;
        setSelectedItems(value);
    };

    const handleClose = () => {
        setShowDataTable(false);
        setPrintData(null);
        setGroupedData({});
        setSelectedItems([]);
        onClose();
    };

    const getMealTypeColor = (mealType) => {
        switch (mealType) {
            case 'Breakfast': return '#FF9800';
            case 'Lunch': return '#4CAF50';
            case 'Snacks': return '#FF5722';
            case 'Dinner': return '#9C27B0';
            default: return '#667eea';
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    height: '80vh'
                }
            }}
        >
            <GradientHeader sx={{ p: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <PrintIcon sx={{ fontSize: 20 }} />
                        <Typography variant="h6" fontWeight="bold">
                            Ingredients Requirements
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </GradientHeader>

            <Box sx={{ p: 1, height: 'calc(100% - 80px)', overflow: 'auto' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={2}>
                            <DatePicker
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EventIcon fontSize="small" />
                                        Date
                                    </Box>
                                }
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                format="dd/MM/yyyy"
                                slotProps={{
                                    textField: {
                                        variant: 'outlined',
                                        fullWidth: true,
                                        sx: { borderRadius: 2 }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={2}>
                            <FormControl fullWidth variant='filled' className="custom-select">
                                <InputLabel id="person-label">Select Person</InputLabel>
                                <Select
                                    labelId="person-label"
                                    id="Person"
                                    name="Person"
                                    className="custom-textfield"
                                    multiple
                                    value={selectedItems}
                                    onChange={handlePersonChange}
                                    label="Select Person"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const item = foodBomItem.find(i => i.Name === value);
                                                return (
                                                    <Chip
                                                        key={value}
                                                        label={item?.Name || value}
                                                        size="small"
                                                        sx={{ borderRadius: 1, color: 'black', padding: '0px' }}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 300,
                                                '& .MuiMenuItem-root': {
                                                    paddingTop: '0px',
                                                    paddingBottom: '0px',
                                                    minHeight: '20px',
                                                    margin: 0,
                                                },
                                                '& .MuiList-root': {
                                                    paddingTop: 0,
                                                    paddingBottom: 0,
                                                }
                                            }
                                        }
                                    }}
                                >
                                    {foodBomItem.map((item) => (
                                        <MenuItem key={item.Name} value={item.Name}>
                                            <Checkbox checked={selectedItems.indexOf(item.Name) > -1} size="small" />
                                            <Typography sx={{ fontSize: '0.9rem' }}>{item.Name}</Typography>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                onClick={fetchPrintData}
                                startIcon={showDataTable ? <VisibilityIcon /> : <PrintIcon />}
                                disabled={loading}
                                sx={{
                                    background: showDataTable ? 'linear-gradient(45deg, #667eea, #764ba2)' : 'linear-gradient(45deg, #10b981, #059669)',
                                    '&:hover': {
                                        background: showDataTable ? 'linear-gradient(45deg, #764ba2, #667eea)' : 'linear-gradient(45deg, #059669, #047857)',
                                    },
                                    borderRadius: 2,
                                }}
                            >
                                {loading ? 'Loading...' : showDataTable ? 'Get Data' : 'Get Data'}
                            </Button>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            {showDataTable && printData && printData.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handlePrint}
                                        startIcon={<PrintIcon />}
                                        sx={{
                                            textTransform: 'none',
                                            background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #764ba2, #667eea)',
                                            },
                                            borderRadius: 2,
                                        }}
                                    >
                                        Details
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handlePrintSummary}
                                        startIcon={<PrintIcon />}
                                        sx={{
                                            textTransform: 'none',
                                            background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                            '&:hover': {
                                                background: 'linear-gradient(45deg, #764ba2, #667eea)',
                                            },
                                            borderRadius: 2,
                                        }}
                                    >
                                        Summary
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </LocalizationProvider>

                {loading && (
                    <Box sx={{ width: '100%', mt: 2 }}>
                        <LinearProgress sx={{ borderRadius: 2 }} />
                    </Box>
                )}

                {showDataTable && printData && printData.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="h6" sx={{ color: '#764ba2', fontWeight: 'bold' }}>
                                        <VisibilityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                        Data Preview ({printData.length} records)
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 2 }} />

                                <Box sx={{ maxHeight: '370px', overflow: 'auto' }}>
                                    {Object.keys(groupedData).sort().map((date) => {
                                        const dateMeals = groupedData[date];
                                        const firstItem = Object.values(dateMeals).flat()[0] || {};

                                        // Dynamically detect EQ fields from the first item
                                        const personKey = Object.keys(firstItem).find(key => key.startsWith('EQNm')) || 'EQNm0';
                                        const qtyKey = Object.keys(firstItem).find(key =>
                                            key.startsWith('EQ') && !key.startsWith('EQNm')
                                        ) || 'EQ0';

                                        const unitKey = Object.keys(firstItem).find(key => key.startsWith('EQNm') &&
                                            !['EQNm0', 'EQNm4', 'EQNm6'].includes(key)
                                        ) || 'EQNm9';

                                        return (
                                            <Box key={date} sx={{ mb: 2 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        bgcolor: '#f3f4f6',
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        fontWeight: 'bold',
                                                        color: '#4b5563',
                                                        mb: 1
                                                    }}
                                                >
                                                    {formatDisplayDate(date)}
                                                    {firstItem[personKey] && (
                                                        <span style={{ marginLeft: 8, fontWeight: 'bold', color: '#000' }}>
                                                            - {selectedItems}
                                                        </span>
                                                    )}
                                                </Typography>

                                                {Object.keys(dateMeals).sort().map((mealType) => (
                                                    <Box key={mealType} sx={{ mt: 1.5 }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: getMealTypeColor(mealType),
                                                                fontWeight: 'bold',
                                                                mb: 1,
                                                                pl: 1
                                                            }}
                                                        >
                                                            {mealType}
                                                        </Typography>

                                                        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow sx={{ bgcolor: '#f9fafb' }}>
                                                                        <TableCell><strong>Food Name</strong></TableCell>
                                                                        <TableCell><strong>Item Name</strong></TableCell>
                                                                        <TableCell align="center"><strong>No. Of Person</strong></TableCell>
                                                                        <TableCell align="center"><strong>Quantity</strong></TableCell>
                                                                        <TableCell><strong>Remark</strong></TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {dateMeals[mealType].map((item, index) => (
                                                                        <TableRow key={index} hover>
                                                                            <TableCell>{item.FoodName || '-'}</TableCell>
                                                                            <TableCell>{item.ItmName || '-'}</TableCell>
                                                                            <TableCell align="center">
                                                                                {item[personKey] || ''}
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                {item[qtyKey] || 0}
                                                                            </TableCell>
                                                                            <TableCell>{item.Remark || '-'}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Box>
                                                ))}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default PrintDialog;